import Image from "next/image";
import { useEntities } from "@/contexts/entities/EntitiesContext";
import { HtmlRenderer } from "../HtmlRenderer";
import Modal from "../Modal";
import { PerformanceLevels } from "../account/PerformanceLevels";
import ChevronRight from "../icons/ChevronRight";
import XIcon from "../icons/XIcon";
import { useAuth } from "@/contexts/auth/AuthContext";
import { useSavedResources } from "@/contexts/savedResources/SavedResourcesContext";
import { useCallback, useMemo, useState } from "react";
import {
  ResourcePath,
  SavedResourcesAction,
  SavedResourcesToast,
} from "./SavedResourcesToast";
import { ErrorToast } from "./ErrorToast";
import Bookmark from "../icons/Bookmark";
import BookmarkChecked from "../icons/BookmarkChecked";
import Analytics from "@/lib/analytics";
import { SubCompetency } from "@/lib/models";
import { ComponentSkillActivitySection } from "../account/ComponentSkillActivitySection";
import Link from "next/link";
import { handleBreadcrumbNavigationClick } from "@/lib/analytics/navigation";
import { SavedResourcesLocation } from "@/lib/analytics/savedResources";

type OverviewComponentSkillModalProps = {
  onClose: () => void;
  componentSkillId: string;
};

type SavedResourcesToastState = {
  isOpen: boolean;
  currentAction: SavedResourcesAction | null;
};

export const OverviewComponentSkillModal = ({
  onClose,
  componentSkillId,
}: OverviewComponentSkillModalProps) => {
  const { getEntityHierarchyFromId } = useEntities();
  const [savedResourcesToastState, setSavedResourcesToastState] =
    useState<SavedResourcesToastState>({
      isOpen: false,
      currentAction: null,
    });

  const [isErrorToastOpen, setIsErrorToastOpen] = useState<boolean>(false);

  const { componentSkills, addComponentSkill, removeComponentSkill } =
    useSavedResources();
  const { user, openLoginPrompt } = useAuth();

  const savedComponentSkillIds = useMemo(
    () => componentSkills.map((skill) => skill.id),
    [componentSkills],
  );

  const isSaved = savedComponentSkillIds.includes(componentSkillId);

  const handleCloseToast = useCallback(() => {
    setSavedResourcesToastState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleCloseErrorToast = useCallback(() => {
    setIsErrorToastOpen(false);
  }, []);

  const handleSaveComponentSkill =
    (subCompetency: SubCompetency, isSaved: boolean) => async () => {
      if (!user) {
        openLoginPrompt("component_skill");
        return;
      }

      try {
        if (isSaved) {
          await removeComponentSkill(subCompetency.id);
          setSavedResourcesToastState({
            isOpen: true,
            currentAction: SavedResourcesAction.Remove,
          });
          Analytics.handleRemoveFromSavedResourcesEvent(
            getEntityHierarchyFromId(subCompetency.id),
            SavedResourcesLocation.OverviewSkillModal,
          );
        } else {
          await addComponentSkill(subCompetency.id);
          setSavedResourcesToastState({
            isOpen: true,
            currentAction: SavedResourcesAction.Add,
          });
          Analytics.handleAddToSavedResourcesEvent(
            getEntityHierarchyFromId(subCompetency.id),
            SavedResourcesLocation.OverviewSkillModal,
          );
        }
      } catch (e) {
        console.error(e);
        setIsErrorToastOpen(true);
      }
    };

  const componentSkill = getEntityHierarchyFromId(componentSkillId);

  return (
    <>
      <Modal isOpen={!!componentSkillId} onClose={onClose}>
        {!!componentSkill.subCompetency?.name && (
          <div className="max-h-[calc(100vh-32px)] w-[calc(100vw-32px)] overflow-y-scroll rounded-2xl bg-white shadow-[0_10px_10px_rgba(0,12,16,0.10)] medium:max-w-[calc(100vw-48px)] large:max-w-screen-large">
            <div className="sticky top-0 flex items-center justify-between border-b border-tab-border bg-white p-1.5">
              <div className="inline-flex items-center gap-2">
                <div
                  className={`h-[68px] w-[68px] rounded-xl bg-${componentSkill.learnerOutcome?.id}-30 mr-4 flex shrink-0`}
                >
                  <Image
                    width={42}
                    height={48}
                    alt={componentSkill.competency!.name!}
                    src={componentSkill.competency!.icon.sourceUrl!}
                    className="m-auto shrink-0"
                  />
                </div>
                <div>
                  <div className="hidden items-center text-sm font-medium leading-4 text-gray-text small:flex">
                    <Link
                      href={`/?selection=${componentSkill.learnerOutcome!.id}`}
                      className="hover:text-dark-color hover:underline"
                      shallow={true}
                      aria-label="Learner Outcome"
                      onClick={() =>
                        handleBreadcrumbNavigationClick({
                          buttonName:
                            "OverviewComponentSkillModal_Learner Outcome",
                          navigatingTo: componentSkill.learnerOutcome!.id,
                        })
                      }
                    >
                      <span>{componentSkill.learnerOutcome!.name}</span>
                    </Link>

                    <ChevronRight className="h-4 w-4" />
                    <Link
                      href={`/?selection=${componentSkill.competency!.id}`}
                      className="hover:text-dark-color hover:underline"
                      shallow={true}
                      aria-label="Competency"
                      onClick={() =>
                        handleBreadcrumbNavigationClick({
                          buttonName: "OverviewComponentSkillModal_Competency",
                          navigatingTo: componentSkill.competency!.id,
                        })
                      }
                    >
                      <span>{componentSkill.competency!.name}</span>
                    </Link>
                    <ChevronRight className="h-4 w-4" />
                  </div>
                  <Link
                    href={`/?selection=${componentSkill.subCompetency!.id}`}
                    className="hover:text-dark-color hover:underline"
                    shallow={true}
                    aria-label="Component Skill"
                    onClick={() =>
                      handleBreadcrumbNavigationClick({
                        buttonName:
                          "OverviewComponentSkillModal_Component Skill",
                        navigatingTo: componentSkill.subCompetency!.id,
                      })
                    }
                  >
                    <span className="text-base font-medium">
                      {componentSkill.subCompetency!.name}
                    </span>
                  </Link>
                </div>
              </div>
              <div className="mr-1.5 inline-flex gap-2 medium:mr-[18px]">
                <button
                  onClick={handleSaveComponentSkill(
                    componentSkill.subCompetency,
                    isSaved,
                  )}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-tab-border p-1 hover:bg-tab-bg-hover active:bg-tab-bg-press"
                  aria-label={isSaved ? "Unsave" : "Save"}
                >
                  {isSaved ? <BookmarkChecked /> : <Bookmark />}
                </button>
                <button
                  className="rounded-full p-1 hover:bg-black/5 active:bg-black/10"
                  aria-label="Close"
                  onClick={onClose}
                >
                  <XIcon />
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 leading-5 text-gray-text medium:px-6">
              <p className="mb-8 flex items-center font-roobert text-xl font-semibold leading-5 text-dark-color medium:text-2xl">
                {componentSkill.subCompetency!.name}
              </p>
              {!!componentSkill.subCompetency!.description && (
                <HtmlRenderer
                  html={componentSkill.subCompetency!.description}
                />
              )}
              <PerformanceLevels
                subCompetency={componentSkill.subCompetency!}
              />
            </div>
            {!!componentSkill.subCompetency!.activities.length && (
              <ComponentSkillActivitySection
                componentSkill={componentSkill.subCompetency!}
              />
            )}
          </div>
        )}
        <SavedResourcesToast
          isOpen={savedResourcesToastState.isOpen}
          currentAction={savedResourcesToastState.currentAction}
          resourcePath={ResourcePath.ComponentSkill}
          handleClose={handleCloseToast}
        />
        <ErrorToast
          isOpen={isErrorToastOpen}
          handleClose={handleCloseErrorToast}
        />
      </Modal>
    </>
  );
};
